<template>
  <tr :key="document.id">
    <td>
      <v-checkbox
        :value="selected"
        class="ma-0"
        hide-details
        @change="$emit('toggle:selected', document.id)"
      />
    </td>
    <td class="px-0 text-center">
      {{ document.id }}
    </td>
    <td class="pa-0">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          <div class="font-weight-bold">
            <a
              href="#"
              class="pa-3"
              style="display: block"
              @click.prevent="openDocument()"
            >
              {{ document.title }}
            </a>
          </div>
        </v-col>
        <v-col
          v-if="getAcceptSubmittable"
          cols="auto"
        >
          <v-btn
            text
            small
            color="primary"
            :loading="getAcceptSubmitting"
            @click="submitToGetAccept"
          >
            <v-icon
              left
              small
            >
              cloud_upload
            </v-icon>
            Submit to GetAccept
          </v-btn>
        </v-col>
        <v-col
          v-else-if="getAccept"
          cols="auto"
        >
          <v-btn
            text
            small
            color="primary"
            :href="getAccept.preview_url"
          >
            View in GetAccept
          </v-btn>
        </v-col>
        <v-col
          v-if="document.status"
          cols="auto"
        >
          <v-chip
            v-if="document.status"
            :color="statusData[document.status].color"
            class="text-uppercase"
            small
            label
            outlined
          >
            {{ statusData[document.status].label }}
          </v-chip>
        </v-col>
      </v-row>
    </td>
    <td>
      <v-chip
        small
        dark
        label
        outlined
        :color="category ? category.color : undefined"
      >
        {{ category ? category.text : '' }}
      </v-chip>
    </td>
    <td class="text-no-wrap">
      <span v-if="document.user">
        {{ document.user.name || (document.user.owner ? document.user.owner.name : '') }}
      </span>
      <span
        v-else
        class="grey--text"
      >
        n/a
      </span>
    </td>
    <td class="text-no-wrap">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <span v-on="on">
            {{ document.created_at | moment('from') }}
          </span>
        </template>
        {{ document.created_at | moment('D MMM YYYY HH:mm:ss') }}
      </v-tooltip>
    </td>
    <td
      v-if="$can('view-owner-visibility', 'Document')"
      class="text-no-wrap"
    >
      <v-chip
        small
        :color="document.is_owner_visible ? 'orange darken-2' : 'grey lighten-3'"
        :dark="!!document.is_owner_visible"
        @click="$can('edit-owner-visibility', 'Document') ? toggleOwnerVisibility() : undefined"
      >
        <v-icon
          left
          small
        >
          {{ document.is_owner_visible ? 'check' : 'close' }}
        </v-icon>
        {{ document.is_owner_visible ? 'Yes' : 'No' }}
      </v-chip>
    </td>
    <td style="width: 1px">
      <v-btn
        v-if="$can('delete-all-documents', 'Document') || document.user_id === $store.state.auth.user.id"
        icon
        small
        color="red"
        @click="triggerDelete()"
      >
        <v-icon small>
          close
        </v-icon>
      </v-btn>
    </td>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete the document?"
      @confirmed="deleteDocument()"
    />
  </tr>
</template>
<script>
import Document from '@/models/Document'
import File from '@/models/File'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
    name: 'DocumentRow',
  components: { ConfirmDeleteDialog },
  mixins: [ deleteConfirmationMixin ],
    props: {
      document: {
        type: Object,
        required: true
      },
      categories: {
        type: Array,
        default: () => ([])
      },
      selected: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        statusData: Document.statusData,
        getAcceptSubmitting: false
      }
    },
    computed: {
      getAccept() {
        if (!this.document.data || !this.document.data.integrations) return null

        return this.document.data.integrations.get_accept
      },
      getAcceptSubmittable() {
        if (!this.getAccept) return false

        return this.getAccept.id === null
      },
      category() {
        return this.categories.find(c => c.key === this.document.type)
      }
    },
    methods: {
      async submitToGetAccept() {
        this.getAcceptSubmitting = true

        try {
          let document = await Document.api.submitToGetAccept(this.document.id)

          this.$emit('update:document', document)

          this.$store.dispatch('alert', {
            text: 'Document successfully sent to GetAccept!',
            color: 'success',
            multiline: true
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong with submitting to GetAccept: ' + e.message,
            color: 'error',
            multiline: true
          })

          throw e
        } finally {
          this.getAcceptSubmitting = false
        }
      },
      openDocument() {
        File.api.openDocument(this.document.file.id)
      },
      triggerDelete() {
        this.deleteConfirmationDialogVisible = true
      },
      async deleteDocument() {
        this.deleting = true

        try {
          await Document.api.delete(this.document.id)

          this.$store.dispatch('alert', {
            text: 'Document deleted',
            color: 'success'
          })

          this.$emit('deleted', this.document.id)
        } catch (e) {
          throw e
        } finally {
          this.deleting = false
          this.deleteConfirmationDialogVisible = false
        }
      },
      async toggleOwnerVisibility() {
        if (!this.$can('edit-owner-visibility', 'Document')) return

        try {
          let document = await Document.api.toggleOwnerVisibility(this.document.id)

          this.$emit('update:document', document)
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        }
      }
    }
  }
</script>
